import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { loadStripe } from '@stripe/stripe-js';
import { useNavigate } from 'react-router-dom';
import { useBillingRequest } from '../services/apis/billing/useBillingRequest';
import { useAppHandlers } from '../services/actions/app/useAppHandlers';
import { useAuthHandlers } from '../services/actions/auth/useAuthHandlers';
import { AppRoutes } from '../interfaces/store/appStore';

interface PaymentVerificationBannerProps {
  onDismiss?: () => void;
  verificationData?: any;
  /**
   * 'verification' (default): the subscription charge failed / needs SCA and
   * access depends on it - urgent framing.
   * 'pending': an already-active subscription has a held plan/seat change waiting
   * on payment confirmation - the current plan is unaffected, gentle framing.
   */
  variant?: 'verification' | 'pending';
}

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_API_KEY || '');

const GENERIC_ERROR = 'We could not verify your payment. Please try again or update your payment method.';

const COPY = {
  verification: {
    emoji: '🔒',
    tag: 'Payment Verification Required',
    cta: 'Verify Payment Now',
    success: 'Payment verified. Thank you!',
  },
  pending: {
    emoji: '💳',
    tag: 'Confirm your purchase',
    cta: 'Confirm payment',
    success: 'Purchase confirmed. Thank you!',
  },
} as const;

const PaymentVerificationBanner: React.FC<PaymentVerificationBannerProps> = ({
  onDismiss,
  variant = 'verification',
}) => {
  const copy = COPY[variant];
  const [isVerifying, setIsVerifying] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const navigate = useNavigate();
  const { getPaymentVerificationIntentRequestHandler, getPaymentVerificationStatusRequestHandler } = useBillingRequest();
  const { showSuccessToast, showErrorToast, showInfoToast } = useAppHandlers();
  const { getUserHandler } = useAuthHandlers();

  const goToPaymentMethods = (message?: string) => {
    setIsVerifying(false);
    if (message) showInfoToast({ message });
    navigate(AppRoutes.SETTINGS_BILLING, { state: { billingTab: 'paymentMethods' } });
  };

  // Verification done - refresh auth/subscription in place so the banner clears
  // and access reflects `active`, without reloading or leaving the current page.
  const finishVerified = async (message: string) => {
    showSuccessToast({ message });
    try {
      await getUserHandler();
    } catch {
      /* the /status poll will still pick it up */
    }
    setIsVerifying(false);
    onDismiss?.();
  };

  // Fallback when we can't confirm a PaymentIntent inline: send the user to the
  // Stripe-hosted invoice page, which handles the card entry and 3D Secure itself.
  const openHostedInvoice = (url: string) => {
    const width = 520;
    const height = 760;
    const left = window.screenX + Math.max(0, (window.outerWidth - width) / 2);
    const top = window.screenY + Math.max(0, (window.outerHeight - height) / 2);
    const popup = window.open(url, 'ld-verify-payment', `width=${width},height=${height},left=${left},top=${top}`);

    if (!popup) {
      // Popup blocked (the await broke the user-gesture chain) - use the tab.
      window.location.href = url;
      return;
    }

    showInfoToast({ message: 'Complete your payment in the Stripe window, then return here.' });
    const poll = setInterval(async () => {
      if (!popup.closed) return;
      clearInterval(poll);
      setIsVerifying(false);

      // The payment clears via a Stripe webhook a beat after the user pays, so a
      // single refetch can race it. Poll the status endpoint (which also nudges
      // the server to reconcile) until it settles, then refresh the app state.
      for (let i = 0; i < 8; i++) {
        try {
          const status = await getPaymentVerificationStatusRequestHandler();
          if (status && !status.paymentVerificationRequired && !status.pendingUpdateExpiresAt) break;
        } catch { /* keep polling */ }
        await new Promise((r) => setTimeout(r, 2000));
      }
      try {
        await getUserHandler();
      } catch { /* the /status poll will still pick it up */ }
      onDismiss?.();
    }, 700);
  };

  const handleVerifyCard = async () => {
    setIsVerifying(true);
    setErrorMessage(null);

    try {
      // The stored client secret is a stale snapshot - always resolve a fresh,
      // confirmable intent from the server against the current open invoice.
      const resolution = await getPaymentVerificationIntentRequestHandler();

      // sendRequest swallows errors and returns undefined - do not mistake a
      // failed request for a resolved verification.
      if (!resolution || typeof resolution.resolved !== 'boolean') {
        setErrorMessage(GENERIC_ERROR);
        showErrorToast({ message: GENERIC_ERROR });
        setIsVerifying(false);
        return;
      }

      if (resolution.resolved) {
        finishVerified(copy.success);
        return;
      }

      // A held plan/seat change: pay it on Stripe's hosted invoice page, which
      // makes Stripe apply the pending change. Don't do the inline 3DS dance here.
      if (variant === 'pending' && resolution.hostedInvoiceUrl) {
        openHostedInvoice(resolution.hostedInvoiceUrl);
        return;
      }

      // No inline-confirmable intent, but there is an open invoice - let the user
      // finish on Stripe's hosted invoice page (handles 3D Secure and new cards).
      if (!resolution.clientSecret && resolution.hostedInvoiceUrl) {
        openHostedInvoice(resolution.hostedInvoiceUrl);
        return;
      }

      if (resolution.requiresNewPaymentMethod || !resolution.clientSecret) {
        goToPaymentMethods('Please update your payment card to continue.');
        return;
      }

      const stripe = await stripePromise;
      if (!stripe) {
        setErrorMessage('Payment service is unavailable. Please try again in a moment.');
        setIsVerifying(false);
        return;
      }

      // Confirm the intent so Stripe runs the pending next action (3D Secure
      // challenge). Pass the card explicitly when the server resolved one, so the
      // call never fails with a missing-payment_method error.
      const { error, paymentIntent } = await stripe.confirmCardPayment(
        resolution.clientSecret,
        resolution.paymentIntentPaymentMethod
          ? { payment_method: resolution.paymentIntentPaymentMethod }
          : undefined,
      );

      if (error) {
        const message = error.message || GENERIC_ERROR;
        setErrorMessage(message);
        showErrorToast({ message });
        setIsVerifying(false);
        return;
      }

      if (paymentIntent && ['succeeded', 'processing', 'requires_capture'].includes(paymentIntent.status)) {
        finishVerified(copy.success);
        return;
      }

      // Authentication finished but the charge still did not go through
      // (e.g. requires_payment_method) - fall back to the hosted invoice, or the
      // payment-methods screen if we don't have its URL.
      if (resolution.hostedInvoiceUrl) {
        openHostedInvoice(resolution.hostedInvoiceUrl);
        return;
      }
      goToPaymentMethods('Please update your payment card to continue.');
    } catch (e: any) {
      const message = e?.message || GENERIC_ERROR;
      setErrorMessage(message);
      showErrorToast({ message });
      setIsVerifying(false);
    }
  };

  return (
    <Container>
      <Overlay />
      <AnimatedBlob $position="left" />
      <AnimatedBlob $position="right" />

      <Content>
        <HeadlineWrapper>
          <Emoji>{copy.emoji}</Emoji>
          <HeadlineText>
            <HeadlineTag>{copy.tag}</HeadlineTag>
            <HeadlineDescription>
              {variant === 'pending'
                ? <>Your bank needs to verify this payment. Your current <Highlight>LeadDelta</Highlight> plan stays active until it&apos;s confirmed.</>
                : <>Your bank needs you to verify this payment to keep your <Highlight>LeadDelta</Highlight> subscription active</>}
            </HeadlineDescription>
            {errorMessage && <ErrorLine>{errorMessage}</ErrorLine>}
          </HeadlineText>
        </HeadlineWrapper>

        <ActionsWrapper>
          <VerifyButton
            onClick={handleVerifyCard}
            disabled={isVerifying}
            type="button"
          >
            {isVerifying ? 'Processing...' : copy.cta}
          </VerifyButton>
          <SecondaryLink
            type="button"
            onClick={() => goToPaymentMethods()}
            disabled={isVerifying}
          >
            Update payment card
          </SecondaryLink>
        </ActionsWrapper>
      </Content>
    </Container>
  );
};

const pulse = keyframes`
  0% {
    opacity: 0.4;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.05);
  }
  100% {
    opacity: 0.4;
    transform: scale(1);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% center;
  }
  100% {
    background-position: 200% center;
  }
`;

const Container = styled.div`
  position: relative;
  overflow: hidden;
  padding: 12px 0;
  background: linear-gradient(135deg, #1a1a2e 0%, #2d1b4e 50%, #4a1942 100%);
  backdrop-filter: blur(10px);
  font-family: 'Nunito Sans', sans-serif;
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.08));
  backdrop-filter: blur(20px);
`;

const AnimatedBlob = styled.div<{ $position: 'left' | 'right' }>`
  position: absolute;
  width: 16rem;
  height: 16rem;
  border-radius: 9999px;
  filter: blur(60px);
  animation: ${pulse} 6s ease-in-out infinite;
  ${({ $position }) =>
    $position === 'left'
      ? `
        top: -2rem;
        left: 25%;
        background: rgba(239, 68, 68, 0.2);
      `
      : `
        bottom: -2rem;
        right: 25%;
        background: rgba(249, 115, 22, 0.2);
        animation-delay: 1s;
      `}
`;

const Content = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  max-width: 78rem;
  margin: 0 auto;
  padding: 0 1rem;
  color: #ffffff;

  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const HeadlineWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const Emoji = styled.span`
  font-size: 2rem;
  filter: drop-shadow(0 0 10px rgba(239, 68, 68, 0.5));
`;

const HeadlineText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const HeadlineTag = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.15rem;
  color: #fca5a5;
`;

const HeadlineDescription = styled.div`
  font-size: 1rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.95);
`;

const Highlight = styled.span`
  color: #fbbf24;
  font-weight: 700;
`;

const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (max-width: 1024px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

const VerifyButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.625rem 1.75rem;
  font-size: 0.875rem;
  font-weight: 700;
  color: #ffffff;
  background: linear-gradient(90deg, #ef4444, #dc2626);
  border-radius: 9999px;
  border: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 10px 30px rgba(239, 68, 68, 0.4);
  cursor: pointer;
  position: relative;
  overflow: hidden;

  &:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: 0 15px 40px rgba(239, 68, 68, 0.5);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  &:not(:disabled)::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );
    background-size: 200% 100%;
    animation: ${shimmer} 2s infinite;
  }
`;

const ErrorLine = styled.div`
  margin-top: 0.35rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #fecaca;
`;

const SecondaryLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.9);
  text-decoration: underline;
  cursor: pointer;

  &:hover:not(:disabled) {
    color: #ffffff;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export default PaymentVerificationBanner;

